/**
 * AccountSettingsPage — Small, focused settings screen currently housing
 * ONE action: permanently delete my account and all associated data.
 *
 * Reuses:
 *  - apiClient, useAuth, useI18n
 *  - existing DELETE /api/auth/me endpoint (cascade delete server-side)
 *
 * Not a general "profile editor" — kept intentionally minimal per the
 * task scope (no new feature, just account cleanup capability).
 */
import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, AlertTriangle, Trash2, Loader2, KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatApiError } from "../lib/api";
import { SAUDI_CITIES, nearestSaudiCity } from "../data/locations";

export default function AccountSettingsPage() {
  const { user, logout } = useAuth();
  const { lang } = useI18n();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  if (user === undefined) return <div className="p-10 text-[#525252]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/settings/account" }} replace />;

  const isAdmin = user.role === "super_admin";
  const requiredWord = lang === "ar" ? "حذف" : "DELETE";
  const canProceed = confirm && typed.trim() === requiredWord && !busy && !isAdmin;

  const doDelete = async () => {
    if (!canProceed) return;
    setBusy(true);
    try {
      await apiClient.delete("/auth/me");
      toast.success(lang === "ar" ? "تم حذف حسابك وكل ما يتعلق به." : "Your account and all associated data have been deleted.");
      // IMPORTANT: navigate FIRST so we're already on a public route,
      // then log out. Otherwise the ProtectedRoute wrapper will kick us
      // to /login before our navigate("/") reaches its target.
      navigate("/", { replace: true });
      await logout();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || (lang === "ar" ? "تعذّر حذف الحساب" : "Failed to delete account"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[720px] mx-auto px-5 py-10" data-testid="account-settings-page">
      <div className="overline overline-gold mb-1">
        {lang === "ar" ? "إعدادات الحساب" : "Account settings"}
      </div>
      <h1 className="font-display text-3xl mb-2">
        {lang === "ar" ? "حسابي" : "My account"}
      </h1>
      <div className="text-[13px] text-[#525252] mb-8">
        {user.email}
      </div>

      {/* iter240 — City section: current city + geolocation shortcut +
          manual pick from the unified SAUDI_CITIES list. Location is
          only read AFTER the user explicitly clicks the button and the
          browser's own permission prompt is granted. Nothing is
          tracked in the background and no coordinates are stored — we
          only save the resulting city slug on the user's profile. */}
      <CitySection lang={lang} user={user} />

      {/* Security — change password */}
      <SecuritySection lang={lang} logout={logout} navigate={navigate} />

      {/* Danger zone */}
      <section
        className="rounded-2xl border p-6"
        style={{ borderColor: "rgba(220,38,38,0.25)", background: "#fff8f8" }}
        data-testid="danger-zone"
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-[#b91c1c]" />
          <h2 className="font-display text-xl text-[#b91c1c]">
            {lang === "ar" ? "منطقة خطرة" : "Danger zone"}
          </h2>
        </div>

        <p className="text-[13px] text-[#525252] leading-relaxed mb-4">
          {lang === "ar"
            ? "حذف حسابك سيحذف نهائيًا: إعلاناتك، أنشطتك التجارية على CARTAL Business ومحتواها بالكامل (المخزون، المنتجات، الحجوزات، طلبات العملاء، المحادثات)، مفضلاتك، تنبيهاتك، وتقييماتك. هذه العملية غير قابلة للتراجع."
            : "Deleting your account permanently removes: your listings, your CARTAL Business tenants and everything inside them (inventory, products, bookings, requests, conversations), favorites, notifications, and reviews. This cannot be undone."}
        </p>

        {isAdmin && (
          <div className="text-[13px] text-[#b91c1c] mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            {lang === "ar"
              ? "حسابك يحمل صلاحية مدير رئيسي (super_admin) — يجب إزالة هذه الصلاحية من قِبَل مدير آخر قبل الحذف."
              : "Your account has super_admin privileges — another admin must demote this account before it can be deleted."}
          </div>
        )}

        {!confirm ? (
          <button
            type="button"
            onClick={() => setConfirm(true)}
            disabled={isAdmin}
            className="px-4 py-2 rounded-lg text-[13px] font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#b91c1c", color: "#fff" }}
            data-testid="delete-account-btn"
          >
            <Trash2 className="w-4 h-4" />
            {lang === "ar" ? "حذف حسابي نهائيًا" : "Delete my account"}
          </button>
        ) : (
          <div className="space-y-3">
            <label className="block text-[12px] font-semibold text-[#b91c1c]">
              {lang === "ar"
                ? `للتأكيد، اكتب كلمة "${requiredWord}" في الحقل التالي:`
                : `Type "${requiredWord}" to confirm:`}
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="w-full rounded-lg border border-red-300 bg-white px-3 py-2 text-[14px] focus:border-red-500 focus:outline-none"
              placeholder={requiredWord}
              autoFocus
              data-testid="delete-confirm-input"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setConfirm(false); setTyped(""); }}
                className="btn-ghost text-[13px]"
                data-testid="delete-cancel-btn"
              >
                {lang === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={doDelete}
                disabled={!canProceed}
                className="px-4 py-2 rounded-lg text-[13px] font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#b91c1c", color: "#fff" }}
                data-testid="delete-confirm-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {lang === "ar" ? "أؤكد الحذف النهائي" : "Confirm permanent deletion"}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ── Security section ────────────────────────────────────────────────
// Change-password form. Requires the user's current password for
// confirmation, enforces an 8-char minimum + confirmation match on
// the client, and forces a fresh login on success (the backend also
// clears auth cookies for good measure).
function SecuritySection({ lang, logout, navigate }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);

  const minLen = 8;
  const lengthOk = next.length >= minLen;
  const matches = next.length > 0 && next === confirmPw;
  const different = next !== current;
  const canSubmit = current.length > 0 && lengthOk && matches && different && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(
        lang === "ar"
          ? "تم تغيير كلمة المرور بنجاح. سيتم تسجيل خروجك للأمان."
          : "Password changed successfully. You'll be signed out for security."
      );
      setCurrent(""); setNext(""); setConfirmPw("");
      // Force re-login with the new password.
      setTimeout(async () => {
        await logout();
        navigate("/login", { replace: true });
      }, 1200);
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
        (lang === "ar" ? "تعذّر تغيير كلمة المرور" : "Failed to change password")
      );
    } finally {
      setBusy(false);
    }
  };

  const fieldCls =
    "w-full rounded-lg border border-[#e5e5e5] bg-white px-3 py-2 pe-10 text-[14px] focus:border-[#a8893e] focus:outline-none";

  return (
    <section
      className="rounded-2xl border p-6 mb-6"
      style={{ borderColor: "rgba(10,10,10,0.10)", background: "#ffffff" }}
      data-testid="security-section"
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-[#a8893e]" />
        <h2 className="font-display text-xl">
          {lang === "ar" ? "الأمان" : "Security"}
        </h2>
      </div>
      <p className="text-[13px] text-[#525252] leading-relaxed mb-5">
        {lang === "ar"
          ? "لحماية حسابك، اختر كلمة مرور قوية لا تقل عن 8 أحرف ولا تُستخدم في مواقع أخرى."
          : "For your protection, choose a strong password of at least 8 characters that you don't reuse elsewhere."}
      </p>

      <form onSubmit={submit} className="space-y-4" autoComplete="off">
        {/* Current password */}
        <div>
          <label className="block text-[12px] font-semibold text-[#404040] mb-1">
            {lang === "ar" ? "كلمة المرور الحالية" : "Current password"}
          </label>
          <div className="relative">
            <input
              type={showCurrent ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={fieldCls}
              autoComplete="current-password"
              data-testid="current-password-input"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((s) => !s)}
              className="absolute inset-y-0 end-2 flex items-center text-[#8a8a8a]"
              aria-label={lang === "ar" ? "إظهار/إخفاء" : "Show/Hide"}
              data-testid="toggle-current-password"
            >
              {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* New password */}
        <div>
          <label className="block text-[12px] font-semibold text-[#404040] mb-1">
            {lang === "ar" ? "كلمة المرور الجديدة" : "New password"}
          </label>
          <div className="relative">
            <input
              type={showNext ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={fieldCls}
              autoComplete="new-password"
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowNext((s) => !s)}
              className="absolute inset-y-0 end-2 flex items-center text-[#8a8a8a]"
              aria-label={lang === "ar" ? "إظهار/إخفاء" : "Show/Hide"}
              data-testid="toggle-new-password"
            >
              {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className={`text-[11px] mt-1 ${lengthOk ? "text-[#0f7c3a]" : "text-[#8a8a8a]"}`}>
            {lang === "ar"
              ? `الحد الأدنى ${minLen} أحرف`
              : `Minimum ${minLen} characters`}
          </div>
        </div>

        {/* Confirm */}
        <div>
          <label className="block text-[12px] font-semibold text-[#404040] mb-1">
            {lang === "ar" ? "تأكيد كلمة المرور الجديدة" : "Confirm new password"}
          </label>
          <div className="relative">
            <input
              type={showNext ? "text" : "password"}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className={fieldCls}
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />
          </div>
          {confirmPw.length > 0 && !matches && (
            <div className="text-[11px] mt-1 text-[#b91c1c]" data-testid="confirm-mismatch">
              {lang === "ar" ? "كلمتا المرور غير متطابقتين." : "Passwords don't match."}
            </div>
          )}
          {next.length > 0 && !different && (
            <div className="text-[11px] mt-1 text-[#b91c1c]" data-testid="same-as-current">
              {lang === "ar"
                ? "كلمة المرور الجديدة يجب أن تختلف عن الحالية."
                : "New password must be different from the current one."}
            </div>
          )}
        </div>

        <div className="pt-1">
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-[13px] font-bold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "#0a0a0a", color: "#fff" }}
            data-testid="change-password-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {lang === "ar" ? "تغيير كلمة المرور" : "Change password"}
          </button>
        </div>
      </form>
    </section>
  );
}


// ── iter241 helper: shared city chooser controls (dropdown + use-my-location + search).
// Used by <CitySection> on the account page AND by <CityOnboardingModal>
// so both share exactly the same list + save flow with no duplication.
export function CityChooserControls({ lang, user, onSaved }) {
  const [city, setCity] = useState(user?.city || "");
  const [busy, setBusy] = useState(false);
  const [locBusy, setLocBusy] = useState(false);
  const [locErr, setLocErr] = useState("");
  // iter241 — manual search box below the dropdown that filters the
  // SAME SAUDI_CITIES list on the fly and lets the user pick from a
  // small live-updated result list. Does NOT replace the dropdown.
  const [search, setSearch] = useState("");
  const disabled = busy || locBusy;

  const saveCity = async (nextCity) => {
    setBusy(true);
    try {
      await apiClient.patch("/auth/me", { city: nextCity });
      setCity(nextCity);
      toast.success(lang === "ar" ? "تم تحديث المدينة" : "City updated");
      onSaved && onSaved(nextCity);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || (lang === "ar" ? "تعذّر التحديث" : "Failed to update"));
    } finally {
      setBusy(false);
    }
  };

  const useMyLocation = () => {
    setLocErr("");
    if (!("geolocation" in navigator)) {
      setLocErr(lang === "ar" ? "المتصفح لا يدعم تحديد الموقع" : "Geolocation not supported");
      return;
    }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const nearest = nearestSaudiCity(latitude, longitude);
          if (!nearest) {
            setLocErr(lang === "ar" ? "تعذّر مطابقة الموقع بمدينة" : "Could not match your location to a city");
          } else {
            await saveCity(nearest);
          }
        } finally { setLocBusy(false); }
      },
      (err) => {
        setLocBusy(false);
        if (err && err.code === 1) {
          setLocErr(lang === "ar" ? "تم رفض الإذن — يمكنك اختيار مدينتك يدوياً." : "Permission denied — pick your city manually.");
        } else {
          setLocErr(lang === "ar" ? "تعذّر قراءة الموقع" : "Failed to read location");
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };

  // Live filter — case-insensitive across id, en, and ar labels.
  const searchResults = search.trim().length >= 1
    ? (() => {
        const needle = search.trim().toLowerCase();
        return SAUDI_CITIES.filter(([id, en, ar]) =>
          id.toLowerCase().includes(needle) ||
          en.toLowerCase().includes(needle) ||
          ar.includes(search.trim())
        ).slice(0, 8);
      })()
    : [];

  return (
    <div>
      {/* 1) Existing dropdown — untouched. */}
      <select
        className="input-dark text-[13px] py-2 w-full mb-3"
        value={city}
        onChange={(e) => saveCity(e.target.value)}
        disabled={disabled}
        data-testid="account-city-select"
      >
        <option value="">{lang === "ar" ? "اختر مدينتك…" : "Pick your city…"}</option>
        {SAUDI_CITIES.map(([id, en, ar]) => (
          <option key={id} value={id}>{lang === "ar" ? ar : en}</option>
        ))}
      </select>

      {/* 2) Side-by-side: [use my location] [search input]. */}
      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          onClick={useMyLocation}
          disabled={disabled}
          className="px-4 py-2 rounded-lg text-[13px] font-bold flex items-center gap-2 disabled:opacity-40"
          style={{ background: "#c9a55c", color: "#0a0a0a" }}
          data-testid="account-city-locate"
        >
          {locBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
          {lang === "ar" ? "استخدم موقعي" : "Use my location"}
        </button>
        <div className="relative flex-1 min-w-[160px]">
          <input
            className="input-dark text-[13px] py-2 w-full"
            type="text"
            placeholder={lang === "ar" ? "🔍 ابحث عن مدينتك…" : "🔍 Search your city…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={disabled}
            data-testid="account-city-search"
          />
          {searchResults.length > 0 && (
            <ul
              className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border shadow-lg bg-white"
              style={{ borderColor: "rgba(0,0,0,0.12)" }}
              data-testid="account-city-search-results"
            >
              {searchResults.map(([id, en, ar]) => (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => { saveCity(id); setSearch(""); }}
                    className="w-full text-start px-3 py-2 text-[13px] hover:bg-[#faf5e8]"
                    data-testid={`account-city-search-opt-${id}`}
                  >
                    {lang === "ar" ? ar : en}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-[#8a8a8a]" />}
      </div>
      {locErr && (
        <div className="mt-3 text-[12px] text-[#b91c1c]" data-testid="account-city-loc-err">
          {locErr}
        </div>
      )}
    </div>
  );
}


// ── iter240: user-side city picker + geolocation shortcut ──────────
function CitySection({ lang, user }) {
  const currentLabel = (() => {
    if (!user?.city) return null;
    const row = SAUDI_CITIES.find(([id]) => id === user.city);
    if (!row) return user.city;
    return lang === "ar" ? row[2] : row[1];
  })();

  return (
    <section className="rounded-2xl border p-6 mb-6" style={{ borderColor: "rgba(0,0,0,0.08)", background: "#fff" }} data-testid="account-city">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-5 h-5 text-[#8a6f2e]" />
        <h2 className="font-display text-xl">{lang === "ar" ? "المدينة" : "City"}</h2>
      </div>
      <p className="text-[13px] text-[#525252] mb-4 leading-relaxed">
        {lang === "ar"
          ? "نستخدم مدينتك لعرض إعلانات وخدمات أقرب إليك. لن نشارك موقعك مع أي مستخدم آخر، ولن نتتبعك في الخلفية."
          : "We use your city to surface listings and services closer to you. Your location is never shared with other users and we do not track you in the background."}
      </p>
      <div className="text-[13px] mb-3" data-testid="account-city-current">
        <span className="text-[#8a8a8a]">{lang === "ar" ? "المدينة الحالية:" : "Current city:"}</span>{" "}
        <span className="font-semibold text-[#0a0a0a]">
          {currentLabel || (lang === "ar" ? "غير محدَّدة" : "Not set")}
        </span>
      </div>
      <CityChooserControls lang={lang} user={user} />
    </section>
  );
}
